/**
 * WorkshopsCalendar — the full schedule card from the Regional / Teacher
 * overview. Card chrome + controls (WorkshopsCalendarHeader) wrapped around the
 * Month/Week grid (WorkshopsCalendarGrid) or the List of shared WorkshopList
 * cards (WorkshopsCalendarListView).
 *
 * Holds no state of its own: the page passes the view, the date and whether
 * cancelled workshops show (see `useCalendarState`), the already-filtered
 * workshops, and any filter controls to draw in the header, and hears about
 * every click. Consumers pass Nebari's
 * pre-joined `WorkshopChip[]` (the same shape both overviews already build)
 * and mount it directly — it renders its
 * own `nb-card` chrome, so no `SectionCard` wrapper. Dark-theme aware via
 * `--nb-*` tokens. Port of the design bundle's `WorkshopsCalendar.jsx`,
 * re-typed off the window-global data model. Date math is UTC (see BON-69).
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import { ALL_CALENDAR_VIEWS } from './use-calendar-state';
import { useCalendarVisibility } from './use-calendar-visibility';
import { DEFAULT_WEEK_STARTS_ON, type WeekStartsOn } from './workshops-calendar-dates';
import { periodLabel } from './workshops-calendar-period';
import {
  WorkshopsCalendarRenderProvider,
  type RenderWorkshopSlot,
} from './workshops-calendar-render-context';
import { WorkshopsCalendarBody } from './WorkshopsCalendarBody';
import { WorkshopsCalendarHeader } from './WorkshopsCalendarHeader';
import type {
  CalendarView,
  DayClickHandler,
  WorkshopChip,
  WorkshopClickHandler,
} from './workshopsCalendarTypes';

export type { WorkshopChip } from './workshopsCalendarTypes';

export interface WorkshopsCalendarProps {
  /** What to draw, already filtered by the page. */
  workshops: WorkshopChip[];
  title?: string;
  views?: CalendarView[];
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  /** A day in the month or week to show. */
  anchor: Date;
  onNav: (dir: 'prev' | 'next') => void;
  /** Whether cancelled workshops are drawn. The header's toggle reports clicks. */
  showCancelled: boolean;
  onToggleCancelled: () => void;
  /** Filter controls for the header, e.g. a `SubregionLegend`. */
  filters?: ReactNode;
  /** The List's message when nothing is coming up. */
  emptyLabel?: string;
  /** Override for "now" — anchors the navigator and the today highlight (tests). */
  today?: Date;
  /** 0 = Sunday … 6 = Saturday. Defaults to Sunday, like every other Nebari calendar. */
  weekStartsOn?: WeekStartsOn;
  onDayClick?: DayClickHandler;
  onWorkshopClick?: WorkshopClickHandler;
  /** When provided, a "New workshop" action shows in the header (BON-203).
   *  Omit in read-only shells (teacher) so the action is absent. */
  onCreateWorkshop?: () => void;
  /** Stretch to fill a flex parent, so the card reaches the bottom of the
   *  space it is given instead of stopping where its rows end. */
  fill?: boolean;
  /**
   * Replaces the default `WorkshopsCalendarChip` for every workshop in the
   * Month/Week grid. Omit for the default chip; the List view's click rule
   * follows the same signal (see `WorkshopsCalendarListView`).
   */
  renderWorkshop?: RenderWorkshopSlot;
  /** Replaces the auto-computed "X workshops in <Month>" count line, given the
   *  workshops in the displayed month or week. Omit for the default. */
  countLineFor?: (inPeriod: WorkshopChip[]) => string;
  /** List view only: the server's status as a pacing flag on each card.
   *  Off by default — decided per surface, never from who is viewing. */
  showPacing?: boolean;
  /** Whether the viewer's own chips take the "mine" highlight in Month/Week,
   *  which replaces their sub-region colour. Default true; the List marks them
   *  with a ring and tag either way. */
  highlightMineInGrid?: boolean;
}

const CARD_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  minHeight: 0,
  overflow: 'hidden',
};

export function WorkshopsCalendar({
  workshops,
  title = 'Upcoming Workshops',
  views = ALL_CALENDAR_VIEWS,
  view,
  onViewChange,
  anchor,
  onNav,
  showCancelled,
  onToggleCancelled,
  filters,
  emptyLabel,
  today,
  weekStartsOn = DEFAULT_WEEK_STARTS_ON,
  onDayClick,
  onWorkshopClick,
  onCreateWorkshop,
  fill = false,
  renderWorkshop,
  countLineFor,
  showPacing,
  highlightMineInGrid,
}: WorkshopsCalendarProps): ReactElement {
  const now = today ?? new Date();

  const cal = useCalendarVisibility({
    workshops,
    view,
    anchor,
    weekStartsOn,
    now,
    countLineFor,
    showCancelled,
    onToggleCancelled,
  });
  // When every workshop is the viewer's own, a "Yours" tag on every card says nothing.
  const allMine = cal.shown.length > 0 && cal.shown.every((w) => w.mine);

  return (
    <WorkshopsCalendarRenderProvider value={renderWorkshop}>
      <section
        data-component="WorkshopsCalendar"
        className="nb-card nb-card-tight"
        style={fill ? { ...CARD_STYLE, flex: 1 } : CARD_STYLE}
      >
        <WorkshopsCalendarHeader
          title={title}
          countLine={cal.countLine}
          filters={filters}
          view={view}
          periodLabel={periodLabel(view, anchor, weekStartsOn)}
          onNav={onNav}
          views={views}
          onSetView={onViewChange}
          onCreateWorkshop={onCreateWorkshop}
        />
        <WorkshopsCalendarBody
          view={view}
          workshops={view === 'List' ? cal.inPeriod : cal.shown}
          anchor={anchor}
          emptyLabel={emptyLabel}
          showTag={allMine ? false : undefined}
          today={today}
          weekStartsOn={weekStartsOn}
          showPacing={showPacing}
          highlightMineInGrid={highlightMineInGrid}
          onDayClick={onDayClick}
          onWorkshopClick={onWorkshopClick}
        />
      </section>
    </WorkshopsCalendarRenderProvider>
  );
}
